import React, { useState } from 'react';

import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import WorkflowArticleLayout from './components/WorkflowArticleLayout';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowDirectoryTree, { TreeNode } from './components/WorkflowDirectoryTree';
import WorkflowLearningPoints from './components/WorkflowLearningPoints';
import WorkflowSection from './components/WorkflowSection';
import WorkflowSeriesComplete from './components/WorkflowSeriesComplete';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';

const beSteps = [
  {
    label: 'Part1',
    title: 'スキーマ定義とAPI仕様書を作成する',
    path: '/devdocs/workflow/be-step1',
  },
  { label: 'Part2', title: 'API実装ワークフローとその全体像', path: '/devdocs/workflow/be-step2' },
  { label: 'Part3', title: 'Phase1: DD書の作成', path: '/devdocs/workflow/be-step3' },
  { label: 'Part4', title: 'Phase2: コード生成', path: '/devdocs/workflow/be-step4' },
  { label: 'Part5', title: 'Phase3: 単体テスト', path: '/devdocs/workflow/be-step5' },
  { label: 'Part6', title: 'E2Eテスト', path: '/devdocs/workflow/be-step6' },
  {
    label: '付録',
    title: '成果物サンプル集',
    path: '/devdocs/workflow/be-appendix',
    active: true,
  },
];

// --- ディレクトリツリー定義 ---
const SAMPLE_TREE: TreeNode[] = [
  {
    name: '00_共通',
    color: '#7b1fa2',
    defaultOpen: true,
    children: [
      { name: '用語集.md', fileId: 'glossary' },
      { name: '設計書Rv指摘.md', fileId: 'rv-design' },
      { name: '設計書Rv指摘_詳細.md', fileId: 'rv-design-detail' },
      { name: '実装Rv指摘.md', fileId: 'rv-impl' },
      { name: '実装Rv指摘_詳細.md', fileId: 'rv-impl-detail' },
    ],
  },
  {
    name: '20_BD',
    color: '#1565c0',
    defaultOpen: true,
    children: [
      {
        name: '10_スキーマ定義',
        defaultOpen: true,
        children: [{ name: '02_002__partner.md', fileId: 'schema-partner' }],
      },
      {
        name: '20_API一覧',
        defaultOpen: true,
        children: [{ name: 'API一覧.md', fileId: 'api-list' }],
      },
      {
        name: '30_API仕様書',
        defaultOpen: true,
        children: [{ name: 'API3001_パートナー登録API.md', fileId: 'api-3001' }],
      },
      {
        name: '40_権限',
        defaultOpen: true,
        children: [{ name: '権限一覧.md', fileId: 'permission-list' }],
      },
      {
        name: '50_プロパティ',
        defaultOpen: true,
        children: [{ name: 'プロパティ一覧.md', fileId: 'property-list' }],
      },
    ],
  },
  {
    name: '30_DD',
    color: '#2e7d32',
    defaultOpen: true,
    children: [
      {
        name: '00_template',
        defaultOpen: false,
        children: [
          { name: 'CreatePersonUseCase.md', fileId: 'tmpl-usecase' },
          { name: 'Person.md', fileId: 'tmpl-model' },
          { name: 'PersonController.md', fileId: 'tmpl-controller' },
          { name: 'PersonMapper.md', fileId: 'tmpl-mapper' },
          { name: 'PersonRepository.md', fileId: 'tmpl-repository' },
          { name: 'PersonService.md', fileId: 'tmpl-service' },
          { name: 'PersonProperties.md', fileId: 'tmpl-properties' },
          { name: 'DuplicateException.md', fileId: 'tmpl-exception' },
          { name: 'PersonType.md', fileId: 'tmpl-const' },
        ],
      },
      {
        name: '10_model',
        defaultOpen: true,
        children: [{ name: 'Partner.md', fileId: 'dd-model-partner' }],
      },
      {
        name: '20_usecase',
        defaultOpen: true,
        children: [{ name: 'CreatePartnerUsecase.md', fileId: 'dd-usecase-create-partner' }],
      },
      {
        name: '40_repository',
        defaultOpen: true,
        children: [
          { name: 'PartnerRepository.md', fileId: 'dd-repository-partner' },
          {
            name: 'mapper',
            defaultOpen: true,
            children: [{ name: 'PartnerMapper.md', fileId: 'dd-mapper-partner' }],
          },
        ],
      },
      {
        name: '50_controller',
        defaultOpen: true,
        children: [{ name: 'PartnerController.md', fileId: 'dd-controller-partner' }],
      },
      {
        name: '90_properties',
        defaultOpen: true,
        children: [{ name: 'PartnerProperties.md', fileId: 'dd-properties-partner' }],
      },
    ],
  },
];

// --- fileId ごとのコンテンツデータ ---
const FILE_CONTENTS: Record<string, { title: string; code: string }> = {
  'schema-partner': {
    title: '02_002__partner.md — partnerテーブルの定義',
    code: `# テーブル定義

## テーブル名

| 論理名     | 物理名  |
|------------|---------|
| パートナー | partner |

## 概要

パートナー（メンバーが所属する企業）を管理する。

## テーブル定義

| 論理名           | 物理名              | 種別           | 閾値 | Not Null | PK | 備考                      |
|------------------|---------------------|----------------|------|----------|----|---------------------------|
| パートナーID     | partner_id          | varchar(64)    |      | 〇       | 〇 | UUID                      |
| パートナー名     | partner_name        | varchar(300)   |      | 〇       |    |                           |
| パートナー名カナ | partner_name_kana   | varchar(300)   |      | 〇       |    |                           |
| パートナー区分ID | partner_type_id     | varchar(300)   |      | 〇       |    | master_type.type_code参照 |
| 法人番号         | corporate_number    | varchar(20)    |      |          |    |                           |
| 登録番号         | invoice_number      | varchar(20)    |      |          |    | インボイス番号（T+13桁）  |
| 住所             | address             | varchar(500)   |      |          |    |                           |
| 電話番号         | phone_number        | varchar(20)    |      |          |    |                           |
| メールアドレス   | mail_address        | varchar(300)   |      |          |    |                           |
| 担当者名         | contact_name        | varchar(300)   |      |          |    | パートナー側窓口          |
| 契約開始日       | contract_start_date | datetime       |      | 〇       |    |                           |
| 契約終了日       | contract_end_date   | datetime       |      |          |    | NULLの場合は契約中        |
| 備考             | remarks             | varchar(10000) |      |          |    |                           |
| 登録日           | created_at          | datetime       |      | 〇       |    |                           |
| 登録者           | created_by          | varchar(300)   |      | 〇       |    | user_id                   |

## unique key

なし

## index

| index名               | column            | seq |
|-----------------------|-------------------|-----|
| partner_name_kana_idx | partner_name_kana | 1   |
| partner_type_id_idx   | partner_type_id   | 1   |
| contract_end_date_idx | contract_end_date | 1   |`,
  },
  'api-3001': {
    title: 'API3001_パートナー登録API.md',
    code: `# API仕様

## 変更履歴

| バージョン | 日付     | diff |
|------------|----------|------|
| v1         | 20260726 | diff |
| v2         | 20260728 | diff |
| v3         | 20260728 | diff |

## 概要

| API ID  | API名             | API概要          | Method | Endpoint           |
|---------|-------------------|------------------|--------|--------------------|
| API3001 | パートナー登録API | パートナーの登録 | POST   | /v1/partner/create |

## 権限

| 権限        |
|-------------|
| ADMIN       |
| BACK_OFFICE |
| MANAGER     |

## Request

### Header

| Key          | Value            | 備考 |
|--------------|------------------|------|
| Content-Type | application/json |      |
| Accept       | application/json |      |
| X-Session-Id | ログインAPIで発行されたセッションID | |

### Body

| 論理名           | 物理名            | type     | NotNull | min | max   | format                | 備考                     |
|------------------|-------------------|----------|---------|-----|-------|-----------------------|--------------------------|
| パートナー名     | partnerName       | string   | 〇      | -   | 300   |                       |                          |
| パートナー名カナ | partnerNameKana   | string   | 〇      | -   | 300   |                       |                          |
| パートナー区分ID | partnerTypeId     | string   | 〇      | -   | 300   |                       | master_type.type_code    |
| 法人番号         | corporateNumber   | string   |         | -   | 20    |                       |                          |
| 登録番号         | invoiceNumber     | string   |         | -   | 20    |                       | インボイス番号（T+13桁） |
| 住所             | address           | string   |         | -   | 500   |                       |                          |
| 電話番号         | phoneNumber       | string   |         | -   | 20    |                       |                          |
| メールアドレス   | mailAddress       | string   |         | -   | 300   |                       |                          |
| 担当者名         | contactName       | string   |         | -   | 300   |                       | パートナー側窓口         |
| 契約開始日       | contractStartDate | datetime | 〇      | -   | -     | yyyy-MM-dd'T'HH:mm:ss |                          |
| 備考             | remarks           | string   |         | -   | 10000 |                       |                          |

## Response

### Status Code

| Error                | Code | 備考                   |
|----------------------|------|------------------------|
| バリデーションエラー | 400  |                        |
| 認証エラー           | 401  | セッションが存在しない |
| 認可エラー           | 403  | 権限が足りない         |

### Body

| 論理名       | 物理名    | type   | NotNull | min | max | format | 備考 |
|--------------|-----------|--------|---------|-----|-----|--------|------|
| パートナーID | partnerId | string | 〇      | -   | -   |        |      |

## 処理概要

1. パートナー区分IDのバリデーション:
    - MasterTypeRepositoryでパートナー区分の一覧を取得する。
    - PartnerTypeGroup#getByCodeで該当するPartnerTypeを取得する。
    - 取得結果がnullの場合、バリデーションエラー（400）を返却する。
2. パートナーIDをUUIDで自動採番する。
3. 登録日に現在日時、登録者にSessionContextHolderのユーザーIDを設定する。
4. partnerテーブルにBodyの値を登録する。
5. 生成したパートナーIDを返却する。`,
  },
  'dd-usecase-create-partner': {
    title: 'CreatePartnerUsecase.md',
    code: `# CreatePartnerUsecase

| class name                  | layer  | package                                      |
|-----------------------------|--------|----------------------------------------------|
| CreatePartnerUsecase.java   | domain | jp.co.ardito.inhouse.kanzhong.domain.usecase |

## components

| component            |
|----------------------|
| MasterTypeRepository |
| PartnerRepository    |
| UUIDService          |
| DateTimeService      |

## field

| name | type |
|------|------|

## methods

### #usecase

#### 引数

| name | type        | description |
|------|-------------|-------------|
| body | API仕様書参照 | RequestBody |

#### 戻り値

| name | type        | description |
|------|-------------|-------------|
| ---  | API仕様書参照 | ResponseDto |

#### throws

| reason                    | type                | description |
|---------------------------|---------------------|-------------|
| パートナー区分ID不正時    | ValidationException | 400         |

#### 処理

1. SessionContextHolder.get().getUserId() から userId を取得する。
2. MasterTypeRepository#fetchPartnerType でパートナー区分の一覧を取得する。
3. PartnerTypeGroup#getByCode を body.partnerTypeId で呼び出し、PartnerType を取得する。
4. 取得結果がnullの場合、バリデーションエラー（400）を返却する。
5. UUIDService#generate でパートナーIDを採番する。
6. DateTimeService#now で現在日時を取得する。
7. Partner を生成する。
    - partnerId: 採番したパートナーID
    - partnerName: body.partnerName
    - partnerNameKana: body.partnerNameKana
    - partnerType: 手順3で取得したPartnerType
    - corporateNumber: body.corporateNumber
    - invoiceNumber: body.invoiceNumber
    - address: body.address
    - phoneNumber: body.phoneNumber
    - mailAddress: body.mailAddress
    - contactName: body.contactName
    - contractStartDate: body.contractStartDate
    - contractEndDate: null
    - remarks: body.remarks
    - createdAt: 現在日時
    - createdBy: 手順1で取得したuserId
8. PartnerRepository#insert を生成した Partner で呼び出す。
9. レスポンスを組み立てて返却する。
    - partnerId: 採番したパートナーID`,
  },
  'dd-model-partner': {
    title: 'Partner.md',
    code: `# Partner

| class name   | layer  | package                                    |
|--------------|--------|--------------------------------------------|
| Partner.java | domain | jp.co.ardito.inhouse.kanzhong.domain.model |

## components

| component |
|-----------|

## field

| name              | type          |
|-------------------|---------------|
| partnerId         | String        |
| partnerName       | String        |
| partnerNameKana   | String        |
| partnerType       | PartnerType   |
| corporateNumber   | String        |
| invoiceNumber     | String        |
| address           | String        |
| phoneNumber       | String        |
| mailAddress       | String        |
| contactName       | String        |
| contractStartDate | LocalDateTime |
| contractEndDate   | LocalDateTime |
| remarks           | String        |
| createdAt         | LocalDateTime |
| createdBy         | String        |
| createdByName     | String        |

## methods

### #getDisplayCreatedByName

#### 引数

| name | type | description |
|------|------|-------------|

#### 戻り値

| name | type   | description    |
|------|--------|----------------|
| ---  | String | 表示用登録者名 |

#### 処理

1. createdByName が null の場合、createdBy を返却する。
2. createdByName を返却する。`,
  },
  'dd-controller-partner': {
    title: 'PartnerController.md',
    code: `# PartnerController

| class name              | layer | package                                      |
|-------------------------|-------|----------------------------------------------|
| PartnerController.java  | web   | jp.co.ardito.inhouse.kanzhong.web.controller |

## components

| component            |
|----------------------|
| CreatePartnerUsecase |
| UpdatePartnerUsecase |
| GetPartnerUsecase    |
| ListPartnerUsecase   |

## field

| name | type |
|------|------|

## methods

### #createPartner

#### エンドポイント

| Method | Endpoint           | Content-Type     |
|--------|--------------------|------------------|
| POST   | /v1/partner/create | application/json |

#### リクエストボディ

API仕様書（API3001）を参照

#### レスポンスボディ

API仕様書（API3001）を参照

#### 処理

1. CreatePartnerUsecase#usecase を RequestBody で呼び出す。
2. 戻り値をレスポンスボディとして返却する。

### #updatePartner

#### エンドポイント

| Method | Endpoint           | Content-Type     |
|--------|--------------------|------------------|
| POST   | /v1/partner/update | application/json |

#### リクエストボディ

API仕様書（API3002）を参照

#### レスポンスボディ

API仕様書（API3002）を参照

#### 処理

1. UpdatePartnerUsecase#usecase を RequestBody で呼び出す。
2. 戻り値をレスポンスボディとして返却する。

### #getPartner

#### エンドポイント

| Method | Endpoint        | Content-Type     |
|--------|-----------------|------------------|
| POST   | /v1/partner/get | application/json |

#### 処理

1. GetPartnerUsecase#usecase を RequestBody で呼び出す。
2. 戻り値をレスポンスボディとして返却する。

### #listPartner

#### エンドポイント

| Method | Endpoint         | Content-Type     |
|--------|------------------|------------------|
| POST   | /v1/partner/list | application/json |

#### 処理

1. ListPartnerUsecase#usecase を RequestBody で呼び出す。
2. 戻り値をレスポンスボディとして返却する。`,
  },
  'dd-repository-partner': {
    title: 'PartnerRepository.md — Interface + Impl',
    code: `# PartnerRepository

## Interface

| class name               | layer  | package                                         |
|--------------------------|--------|-------------------------------------------------|
| PartnerRepository.java   | domain | jp.co.ardito.inhouse.kanzhong.domain.repository |

## Impl

| class name                   | layer | package                                        |
|------------------------------|-------|------------------------------------------------|
| PartnerRepositoryImpl.java   | infra | jp.co.ardito.inhouse.kanzhong.infra.repository |

## components

| component           |
|---------------------|
| PartnerMapper       |
| MasterTypeRepository|

## methods

### #fetchById

#### 引数

| name      | type   | description  |
|-----------|--------|--------------|
| partnerId | String | パートナーID |

#### 戻り値

| name | type    | description            |
|------|---------|------------------------|
| ---  | Partner | パートナー（nullあり） |

#### 処理

1. PartnerMapper#findById を partnerId で呼び出す。
2. 取得結果がnullの場合、nullを返却する。
3. MasterTypeRepository#fetchPartnerType でパートナー区分の一覧を取得する。
4. PartnerTypeGroup#getByCode を PartnerEntity.partnerTypeId で呼び出す。
5. PartnerEntity を Partner に変換して返却する。

### #insert

#### 引数

| name    | type    | description |
|---------|---------|-------------|
| partner | Partner | パートナー  |

#### 戻り値

| name | type | description |
|------|------|-------------|
| ---  | void |             |

#### 処理

1. Partner を PartnerEntity に変換する（partnerTypeId: Partner.partnerType.code）。
2. PartnerMapper#insert を生成した PartnerEntity で呼び出す。

### #update

#### 引数

| name    | type    | description |
|---------|---------|-------------|
| partner | Partner | パートナー  |

#### 処理

1. Partner を PartnerEntity に変換する。
2. PartnerMapper#update を生成した PartnerEntity で呼び出す。

### #fetchByCondition

#### 引数

| name      | type             | description |
|-----------|------------------|-------------|
| condition | PartnerCondition | 検索条件    |

#### 戻り値

| name | type            | description      |
|------|-----------------|------------------|
| ---  | List<Partner>   | パートナーリスト |

#### 処理

1. PartnerCondition を PartnerConditionEntity に変換する。
2. PartnerMapper#findByCondition を呼び出す。
3. MasterTypeRepository#fetchPartnerType でパートナー区分の一覧を取得する。
4. 取得結果の List を Partner リストに変換して返却する。`,
  },
  'dd-mapper-partner': {
    title: 'PartnerMapper.md — SQL定義 + Entity定義',
    code: `# PartnerMapper

| class name          | layer | package                                    |
|---------------------|-------|--------------------------------------------|
| PartnerMapper.java  | infra | jp.co.ardito.inhouse.kanzhong.infra.mapper |

## 参照DB

- partner
- user

## methods

### #findById

#### 引数

| name      | type   | description  |
|-----------|--------|--------------|
| partnerId | String | パートナーID |

#### 戻り値

| name | type          | description |
|------|---------------|-------------|
| ---  | PartnerEntity | パートナー  |

#### 処理

1. partnerテーブルからパートナーIDで1件取得する。userテーブルとLEFT OUTER JOINして登録者名を取得する。

\`\`\`sql
SELECT
  p.partner_id, p.partner_name, p.partner_name_kana, p.partner_type_id,
  p.corporate_number, p.invoice_number, p.address, p.phone_number,
  p.mail_address, p.contact_name, p.contract_start_date, p.contract_end_date,
  p.remarks, p.created_at, p.created_by, u.user_name AS created_by_name
FROM partner p
LEFT OUTER JOIN user u ON p.created_by = u.user_id
WHERE p.partner_id = #{partnerId}
\`\`\`

### #insert

#### 引数

| name   | type          | description |
|--------|---------------|-------------|
| entity | PartnerEntity | パートナー  |

#### 処理

1. partnerテーブルにパートナーを登録する。

\`\`\`sql
INSERT INTO partner (
  partner_id, partner_name, partner_name_kana, partner_type_id,
  corporate_number, invoice_number, address, phone_number,
  mail_address, contact_name, contract_start_date, contract_end_date,
  remarks, created_at, created_by
) VALUES (
  #{partnerId}, #{partnerName}, #{partnerNameKana}, #{partnerTypeId},
  #{corporateNumber}, #{invoiceNumber}, #{address}, #{phoneNumber},
  #{mailAddress}, #{contactName}, #{contractStartDate}, #{contractEndDate},
  #{remarks}, #{createdAt}, #{createdBy}
)
\`\`\`

### #update

#### 処理

1. partnerテーブルのパートナー情報を更新する。

\`\`\`sql
UPDATE partner SET
  partner_name = #{partnerName}, partner_name_kana = #{partnerNameKana},
  partner_type_id = #{partnerTypeId}, corporate_number = #{corporateNumber},
  invoice_number = #{invoiceNumber}, address = #{address},
  phone_number = #{phoneNumber}, mail_address = #{mailAddress},
  contact_name = #{contactName}, contract_start_date = #{contractStartDate},
  contract_end_date = #{contractEndDate}, remarks = #{remarks}
WHERE partner_id = #{partnerId}
\`\`\`

### #findByCondition（動的SQL）

#### 処理

1. partnerテーブルから条件に合致するパートナーリストを取得する。

\`\`\`sql
SELECT ... FROM partner p
LEFT OUTER JOIN user u ON p.created_by = u.user_id
<where>
  <if test="partnerName != null">AND p.partner_name LIKE CONCAT(#{partnerName}, '%')</if>
  <if test="partnerNameKana != null">AND p.partner_name_kana LIKE CONCAT(#{partnerNameKana}, '%')</if>
  <if test="partnerTypeId != null">AND p.partner_type_id LIKE CONCAT(#{partnerTypeId}, '%')</if>
  <if test="includeExpired == null or !includeExpired">AND (p.contract_end_date IS NULL OR p.contract_end_date >= #{now})</if>
</where>
ORDER BY p.partner_name_kana LIMIT #{limitSize}
\`\`\`

---

# Entity

## PartnerEntity

| class name         | package                                    |
|--------------------|--------------------------------------------|
| PartnerEntity.java | jp.co.ardito.inhouse.kanzhong.infra.entity |

### field

| name              | type          |
|-------------------|---------------|
| partnerId         | String        |
| partnerName       | String        |
| partnerNameKana   | String        |
| partnerTypeId     | String        |
| corporateNumber   | String        |
| invoiceNumber     | String        |
| address           | String        |
| phoneNumber       | String        |
| mailAddress       | String        |
| contactName       | String        |
| contractStartDate | LocalDateTime |
| contractEndDate   | LocalDateTime |
| remarks           | String        |
| createdAt         | LocalDateTime |
| createdBy         | String        |
| createdByName     | String        |

## PartnerConditionEntity

| name            | type          |
|-----------------|---------------|
| partnerName     | String        |
| partnerNameKana | String        |
| partnerTypeId   | String        |
| includeExpired  | Boolean       |
| limitSize       | int           |
| now             | LocalDateTime |`,
  },
  'dd-properties-partner': {
    title: 'PartnerProperties.md',
    code: `# PartnerProperties

| class name              | layer  | package                                         |
|-------------------------|--------|-------------------------------------------------|
| PartnerProperties.java  | domain | jp.co.ardito.inhouse.kanzhong.domain.properties |

## field

| name      | type | description  |
|-----------|------|--------------|
| limitSize | int  | 取得件数上限 |

## methods

なし

## プロパティマッピング

| field     | プロパティキー   | 備考           |
|-----------|------------------|----------------|
| limitSize | partner.limit-size | プロパティ一覧参照 |`,
  },
  'permission-list': {
    title: '権限一覧.md（抜粋）',
    code: `# 権限一覧

## 概要

各APIエンドポイントに必要な権限の一覧。

- 権限なし: ログイン済み（セッション認証のみ）で全ユーザーがアクセス可能
- 権限あり: 記載されたロールのいずれかを持つユーザーのみアクセス可能

## 権限一覧（抜粋）

| API ID  | API名                    | Endpoint              | Method | 必要権限                   |
|---------|--------------------------|-----------------------|--------|----------------------------|
| API1002 | ユーザーセッション取得API | /v1/auth/session      | GET    | なし                       |
| API2001 | ユーザー登録API          | /v1/user/create       | POST   | ADMIN                      |
| API3001 | パートナー登録API        | /v1/partner/create    | POST   | ADMIN, BACK_OFFICE, MANAGER |
| API3002 | パートナー更新API        | /v1/partner/update    | POST   | ADMIN, BACK_OFFICE, MANAGER |
| API3101 | 要員登録API              | /v1/member/create     | POST   | ADMIN, BACK_OFFICE, MANAGER |
| API3104 | 要員取得API              | /v1/member/get        | POST   | なし                       |
| API8006 | 通知一覧取得（ユーザー）API | /v1/notice/list    | POST   | なし                       |
| API9001 | 区分取得API              | /v1/master/type/{typeName} | GET | なし                  |

※ 実際のファイルには全API（50件以上）が記載されています。`,
  },
  'api-list': {
    title: 'API一覧.md（抜粋）',
    code: `# API一覧（抜粋）

| API ID  | API名              | Method | Endpoint              | Controller           | Usecase                  | 概要               |
|---------|--------------------|--------|-----------------------|----------------------|--------------------------|--------------------|
| API1001 | ログインAPI        | POST   | /v1/auth/login        | AuthController       | LoginUsecase             | ユーザーの認証を行う |
| API1002 | ユーザーセッション取得API | GET | /v1/auth/session    | AuthController       | GetUserSessionUsecase    | セッションからユーザー情報を取得 |
| API3001 | パートナー登録API  | POST   | /v1/partner/create    | PartnerController    | CreatePartnerUsecase     | パートナーの登録   |
| API3002 | パートナー更新API  | POST   | /v1/partner/update    | PartnerController    | UpdatePartnerUsecase     | パートナーの更新   |
| API3004 | パートナー取得API  | POST   | /v1/partner/get       | PartnerController    | GetPartnerUsecase        | パートナーの取得   |
| API3005 | パートナー一覧取得API | POST | /v1/partner/list     | PartnerController    | ListPartnerUsecase       | パートナーの一覧取得 |
| API3101 | 要員登録API        | POST   | /v1/member/create     | MemberController     | CreateMemberUsecase      | 要員の登録         |
| API9001 | 区分取得API        | GET    | /v1/master/type/{typeName} | MasterController | GetMasterTypeUsecase    | 区分の取得         |

※ 実際のファイルには全API（50件以上）が記載されています。各セルにはDD書・API仕様書へのリンクが付与されます。`,
  },
  glossary: {
    title: '用語集.md（抜粋）',
    code: `# 用語集（抜粋）

## あ行

| 論理名             | カナ               | 物理名            | 備考 |
|--------------------|--------------------|-------------------|------|
| APIエンドポイント  | APIエンドポイント  | apiEndpoint       |      |
| エンドポイント     | エンドポイント     | endpoint          |      |

## か行

| 論理名   | カナ           | 物理名    | 備考   |
|----------|----------------|-----------|--------|
| 解決     | カイケツ       | resolve   |        |
| 開始日   | カイシビ       | startDate |        |
| 権限     | ケンゲン       | role      |        |
| 権限ID   | ケンゲンID     | roleId    |        |
| 更新     | コウシン       | update    |        |
| 更新日   | コウシンビ     | updatedAt |        |
| 更新者   | コウシンシャ   | updatedBy |        |

※ 実際のファイルにはあ〜わ行の全用語（100件以上）が記載されています。`,
  },
  'property-list': {
    title: 'プロパティ一覧.md',
    code: `# プロパティ一覧

| key                          | 設定値 | 備考         |
|------------------------------|--------|--------------|
| session.limit                | 10080  | 分           |
| user.limit-size              | 100    |              |
| partner.limit-size           | 100    |              |
| member.limit-size            | 100    |              |
| client.limit-size            | 100    |              |
| project.limit-size           | 100    |              |
| contract.limit-size          | 100    |              |
| contract-request.limit-size  | 100    |              |
| notice.deadline-warning-days | 3      | 期限警告日数 |
| notice.new-days              | 7      | 最新日数     |
| assessment.limit-size        | 100    |              |`,
  },
  'rv-design': {
    title: '設計書Rv指摘.md（抜粋）',
    code: `# 設計書Rv指摘（抜粋）

## ルール

- 設計書修正時の指摘内容を記載する
- 指摘ID: ID_1からの連番
- 指摘詳細は設計書Rv指摘_詳細.mdに記載する

## 指摘一覧

| 指摘ID | 仕様書 | バージョン | ファイル | 指摘内容 | 指摘理由 |
|--------|--------|------------|----------|----------|----------|
| ID_29 | API3501_契約申請登録API | v1 | API3501_契約申請登録API.md | 年度・年度月の自動計算をFinancialYearServiceでfinancial_yearテーブルから取得する方式に変更 | 自動計算ではなくマスタテーブルから取得すべき |
| ID_32 | API3501_契約申請登録API | v1 | API3501〜API3503 | IDの存在確認エラーは404ではなく400（バリデーションエラー）にする | 登録APIでのID不正は入力値の不正であり、リソースの非存在ではない |
| ID_33 | API3501_契約申請登録API | v1 | FinancialYearService.md | getByDateはfetchAll+フィルタではなく日付をキーに1件取得するSQLを作成する | 不要な全件取得を避けパフォーマンスを確保する |

※ 実際のファイルにはID_1〜ID_34の全指摘が記載されています。`,
  },
  'rv-design-detail': {
    title: '設計書Rv指摘_詳細.md（抜粋）',
    code: `# 設計書Rv指摘 詳細（抜粋）

## ID_2

### 修正前

Repository のメソッド名に \`findByUserIdAndPassword\`, \`findActiveByUserId\`, \`save\` を使用していた。

### 指摘内容

Repositoryの取得系の動詞は全て \`fetch\`、登録系は全て \`insert\` で統一する。

### 修正内容

- \`findByUserIdAndPassword\` → \`fetchByUserIdAndPassword\`
- \`findActiveByUserId\` → \`fetchActiveByUserId\`
- \`save\` → \`insert\`

## ID_3

### 修正前

Mapper のメソッド名に \`selectByUserIdAndPassword\`, \`selectActiveByUserId\` を使用していた。

### 指摘内容

Mapperの取得系の動詞は全て \`find\` で統一する。

### 修正内容

- \`selectByUserIdAndPassword\` → \`findByUserIdAndPassword\`
- \`selectActiveByUserId\` → \`findActiveByUserId\`

※ 実際のファイルにはID_1〜ID_34の全詳細が記載されています。`,
  },
  'rv-impl': {
    title: '実装Rv指摘.md（抜粋）',
    code: `# 実装Rv指摘（抜粋）

## ルール

- 実装修正時の指摘内容を記載する
- 指摘ID: ID_1からの連番
- 指摘詳細は実装Rv指摘_詳細.mdに記載する

## 指摘一覧

| 指摘ID | 仕様書 | バージョン | ファイル | クラス | 指摘内容 | 指摘理由 |
|--------|--------|------------|----------|--------|----------|----------|
| ID_1 | API3001_パートナー登録API | v1 | mk_instructions.md | CreatePartnerRequestDto, UpdatePartnerRequestDto | RequestDtoに@Size(max)バリデーションが漏れていた | API仕様書にmax桁数が定義されているフィールドには@Sizeを付与する必要がある |
| ID_2 | API3401_契約取得API | v1 | mk_instructions.md | GetContractResultDto | DTOでstatic inner classを利用していた | DTOでInner classは利用しない。ネストDTOは独立したクラスとして配置する |
| ID_3 | API3501_契約申請登録API | v1 | mk_instructions.md | ContractRequest | master_type型のフィールドをStringで定義していた | master_type参照のフィールドは対応するMasterType継承クラスを型に利用する |

※ 実際のファイルには全指摘が記載されています。`,
  },
  'rv-impl-detail': {
    title: '実装Rv指摘_詳細.md（抜粋）',
    code: `# 実装Rv指摘 詳細（抜粋）

## ID_1

### 修正前

ControllerのAuthController#loginで、引数を直接 \`@Valid @RequestBody\`（userId, password）として記載していた。DTOクラスの作成が含まれていなかった。

### 指摘内容

ControllerのRequestDtoとResponseDtoは\`web.controller.dto\`配下に作成する。Objectがネストする場合はstaticクラスは利用せず\`web.controller.dto.model\`配下に作成する。

### 修正内容

- 新規作成するクラスにLoginRequestDto、LoginResponseDtoを追加した。
- AuthController#loginの引数をLoginRequestDto、戻り値をLoginResponseDtoに変更した。

## ID_4

### 修正前

「UUID生成はハイフンなし32文字（\`UUID.randomUUID().toString().replace("-", "")\`）とする。」と記載していた。

### 指摘内容

UUIDはハイフンありで良い。UUID生成のためのUUIDServiceを作成して利用する。

### 修正内容

- UUIDServiceクラスを新規作成クラスに追加した。
- LoginUsecaseの処理内でUUIDService#generateを利用する記載に変更した。

※ 実際のファイルには全指摘の詳細が記載されています。`,
  },
  'tmpl-usecase': {
    title: 'CreatePersonUseCase.md（テンプレート）',
    code: `# CreatePersonUseCase（テンプレート）

| class name               | layer  | package                                      |
|--------------------------|--------|----------------------------------------------|
| CreatePersonUseCase.java | domain | jp.co.ardito.inhouse.kanzhong.domain.usecase |

## components

| component   |
|-------------|
| PersonService |

## field

| name | type |
|------|------|

## methods

### #usecase

#### 引数

| name | type        | description |
|------|-------------|-------------|
| body | API仕様書参照 | RequestBody |

#### 戻り値

| name | type | description |
|------|------|-------------|
| ---  | void |             |

#### 処理

1. 新規Personを生成する。
    - personId: body.personId を設定する。
    - personName: body.personName を設定する。
    - type: PersonType.ADULTS
2. PersonService#createPerson を生成した person で呼び出す。`,
  },
  'tmpl-model': {
    title: 'Person.md（テンプレート）',
    code: `# Person（テンプレート）

| class name  | layer  | package                                    |
|-------------|--------|--------------------------------------------|
| Person.java | domain | jp.co.ardito.inhouse.kanzhong.domain.model |

## components

| component |
|-----------|

## field

| name       | type       |
|------------|------------|
| personId   | String     |
| personName | String     |
| type       | PersonType |

## methods

なし`,
  },
  'tmpl-controller': {
    title: 'PersonController.md（テンプレート）',
    code: `# PersonController（テンプレート）

| class name            | layer | package                                      |
|-----------------------|-------|----------------------------------------------|
| PersonController.java | web   | jp.co.ardito.inhouse.kanzhong.web.controller |

## components

| component          |
|--------------------|
| CreatePersonUseCase |

## field

| name | type |
|------|------|

## methods

### #createPerson

#### エンドポイント

| Method | Endpoint          | Content-Type     |
|--------|-------------------|------------------|
| POST   | /v1/person/create | application/json |

#### リクエストボディ

API仕様書参照

#### レスポンスボディ

API仕様書参照

#### 処理

1. CreatePersonUseCase#usecase を RequestBody で呼び出す。
2. レスポンスボディは空で返却する。`,
  },
  'tmpl-mapper': {
    title: 'PersonMapper.md（テンプレート）',
    code: `# PersonMapper（テンプレート）

| class name        | layer | package                                    |
|-------------------|-------|--------------------------------------------|
| PersonMapper.java | infra | jp.co.ardito.inhouse.kanzhong.infra.mapper |

## 参照DB

- テーブル名（スキーマ定義リンク）

## methods

### #findByPersonId

#### 引数

| name     | type   | description |
|----------|--------|-------------|
| personId | String |             |

#### 戻り値

| name | type         | description                    |
|------|--------------|--------------------------------|
| ---  | PersonEntity | 人物情報（存在しなければnull） |

#### 処理

1. personテーブルから人物IDで検索し、結果を返却する。

\`\`\`sql
SELECT person_id, person_name
FROM person
WHERE person_id = #{personId}
\`\`\`

### #insert

#### 引数

| name   | type         | description |
|--------|--------------|-------------|
| entity | PersonEntity | 人物情報    |

#### 処理

1. personテーブルに人物情報を登録する。

\`\`\`sql
INSERT INTO person (person_id, person_name)
VALUES (#{entity.personId}, #{entity.personName})
\`\`\`

---

# Entity

## PersonEntity

| class name        | package                                    |
|-------------------|--------------------------------------------|
| PersonEntity.java | jp.co.ardito.inhouse.kanzhong.infra.entity |

### field

| name       | type   |
|------------|--------|
| personId   | String |
| personName | String |`,
  },
  'tmpl-repository': {
    title: 'PersonRepository.md（テンプレート）',
    code: `# PersonRepository（テンプレート）

## Interface

| class name            | layer  | package                                         |
|-----------------------|--------|-------------------------------------------------|
| PersonRepository.java | domain | jp.co.ardito.inhouse.kanzhong.domain.repository |

## Impl

| class name                | layer | package                                        |
|---------------------------|-------|------------------------------------------------|
| PersonRepositoryImpl.java | infra | jp.co.ardito.inhouse.kanzhong.infra.repository |

## components

| component    |
|--------------|
| PersonMapper |

## methods

### #fetchByPersonId

#### 引数

| name     | type   | description |
|----------|--------|-------------|
| personId | String |             |

#### 戻り値

| name | type   | description                    |
|------|--------|--------------------------------|
| ---  | Person | 人物情報（存在しなければnull） |

#### 処理

1. PersonMapper#findByPersonId を呼び出す。
2. 取得結果のPersonEntityをPersonに変換して返却する。
3. 取得結果がnullの場合はnullを返却する。

### #insert

#### 引数

| name   | type   | description |
|--------|--------|-------------|
| person | Person | 人物情報    |

#### 処理

1. PersonをPersonEntityに変換する。
2. PersonMapper#insert を呼び出す。`,
  },
  'tmpl-service': {
    title: 'PersonService.md（テンプレート）',
    code: `# PersonService（テンプレート）

| class name         | layer  | package                                      |
|--------------------|--------|----------------------------------------------|
| PersonService.java | domain | jp.co.ardito.inhouse.kanzhong.domain.service |

## components

| component        |
|------------------|
| PersonRepository |

## methods

### #createPerson

#### 引数

| name   | type   | description |
|--------|--------|-------------|
| person | Person | 人物情報    |

#### 戻り値

| name | type | description |
|------|------|-------------|
| ---  | void |             |

#### throws

| reason               | type               | description |
|----------------------|--------------------|-------------|
| 人物IDが存在する場合 | DuplicateException |             |

#### 処理

1. PersonRepository#fetchByPersonId を person.personId で呼び出す。
2. 取得結果がnullでない場合、DuplicateException をスローする。
3. PersonRepository#insert を person で呼び出す。`,
  },
  'tmpl-properties': {
    title: 'PersonProperties.md（テンプレート）',
    code: `# PersonProperties（テンプレート）

| class name            | layer  | package                                         |
|-----------------------|--------|-------------------------------------------------|
| PersonProperties.java | domain | jp.co.ardito.inhouse.kanzhong.domain.properties |

## field

| name  | type | description              |
|-------|------|--------------------------|
| limit | long | セッション有効期限（分） |

## methods

なし

## プロパティマッピング

| field | プロパティキー | 備考           |
|-------|----------------|----------------|
| limit | person.limit   | プロパティ一覧参照 |`,
  },
  'tmpl-exception': {
    title: 'DuplicateException.md（テンプレート）',
    code: `# DuplicateException（テンプレート）

| class name              | layer  | package                                          |
|-------------------------|--------|--------------------------------------------------|
| DuplicateException.java | domain | jp.co.ardito.inhouse.kanzhong.domain.model.error |

## extends

| class            |
|------------------|
| RuntimeException |

## implements

なし

## field

| name | type |
|------|------|

## methods

なし`,
  },
  'tmpl-const': {
    title: 'PersonType.md（テンプレート）',
    code: `# PersonType（テンプレート）

| class name      | layer  | package                                       |
|-----------------|--------|-----------------------------------------------|
| PersonType.java | domain | jp.co.ardito.inhouse.kanzhong.domain.constant |

## field

| name | type   |
|------|--------|
| code | String |
| name | String |

## values

| Enum値   | code     | name |
|----------|----------|------|
| CHILDREN | CHILDREN | 子供 |
| ADULTS   | ADULTS   | 大人 |

## methods

### #isAdults

#### 引数

| name | type | description |
|------|------|-------------|

#### 戻り値

| name | type    | description      |
|------|---------|------------------|
| ---  | boolean | 大人の場合はtrue |

#### 処理

1. this が ADULTSの場合 true を返却
2. それ以外は false を返却`,
  },
};

const WorkflowBeAppendix: React.FC = () => {
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);

  const selectedContent = selectedFileId ? FILE_CONTENTS[selectedFileId] : null;

  return (
    <WorkflowArticleLayout
      title="バックエンド開発編 付録 — 成果物サンプル集"
      seriesName="バックエンド開発編"
      tags={['Backend', 'Sample', 'Reference']}
      seriesSteps={beSteps}
      prevLink={{ label: 'Part6: E2Eテスト', path: '/devdocs/workflow/be-step6' }}
    >
      <WorkflowLearningPoints
        items={[
          'スキーマ定義書・API仕様書の実際の出力例',
          'DD書の種類ごとの実際の出力例',
          '権限一覧・API一覧・用語集・プロパティ定義の実際の出力例',
        ]}
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: '概要' },
          { id: 'section-2', label: '成果物サンプルビューア' },
          { id: 'section-3', label: '注意すべきこと' },
        ]}
      />

      {/* セクション1: 概要 */}
      <WorkflowSection title="概要" id="section-1" number={1}>
        <WorkflowText>
          この記事はバックエンド開発編（Part1〜6）で生成される成果物の実例を集めたリファレンスです。手順の流れは各Part記事を参照してください。ここでは「実際にどんなファイルが出力されるか」を確認できます。
        </WorkflowText>
      </WorkflowSection>

      {/* セクション2: 成果物サンプルビューア */}
      <WorkflowSection title="成果物サンプルビューア" id="section-2" number={2}>
        <WorkflowText>
          サンプルとして掲載するファイルを以下のツリー構造で表示します。クリック可能なファイルを選択すると、下のコードブロックに選択されたファイルに対応するテキストが表示されます。
        </WorkflowText>

        {/* ディレクトリツリー */}
        <WorkflowDirectoryTree
          root="docs/"
          entries={SAMPLE_TREE}
          onFileSelect={setSelectedFileId}
          selectedFileId={selectedFileId ?? undefined}
        />

        {/* 選択ファイルのコンテンツ表示 */}
        {selectedContent ? (
          <WorkflowCodeBlock
            title={selectedContent.title}
            language="markdown"
            code={selectedContent.code}
          />
        ) : (
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              p: 3,
              mt: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              上のツリーからファイルを選択してください
            </Typography>
          </Box>
        )}
      </WorkflowSection>

      {/* セクション3: 注意すべきこと */}
      <WorkflowSection title="注意すべきこと" id="section-3" number={3}>
        <WorkflowTable
          headers={['注意点', '説明']}
          rows={[
            [
              'サンプルは特定時点のスナップショット',
              'プロジェクトの進行に伴い、Steeringルールやフォーマットは改善される。最新は実際のSteeringファイルを参照',
            ],
            [
              'コピペして使わない',
              'サンプルはあくまで「こういう出力になる」の参考。実際の生成はワークフロー経由で行う',
            ],
          ]}
        />
      </WorkflowSection>

      <WorkflowSeriesComplete
        seriesName="バックエンド開発編"
        message="お疲れ様でした。スキーマ定義からE2Eテストまで、全6パート+付録の学習が完了です。"
      />
    </WorkflowArticleLayout>
  );
};

export default WorkflowBeAppendix;
